import React, { memo } from "react";
import PlusIcon from "_c/icon/Plus";
import * as S from "./styled";
import PT from "prop-types";
import CheckmarkIcon from "_c/icon/Checkmark";
import { motion, AnimatePresence } from "framer-motion";
import { useToggle } from "@/hooks/useToggle";
import {
  backdropVariants,
  subButtonVariants,
  eventNameVariants,
} from "./motion";
import { analyticsAction } from "@/utils/analytics";

const ns = "common:new_event_fab.";
const NewEventFab = (props) => {
  const [isOpen, toggle] = useToggle();
  const onClick = (type) => {
    if (type === "personal") {
      props.onPersonalTaskClick();
    } else if (type === "business") {
      props.onTaskClick();
    } else if (type === "todo") {
      props.onTodoClick();
    } else if (type === "expense") {
      props.onExpenseClick();
    } else if (type === "income") {
      props.onIncomeClick();
    } else if (type === "client") {
      props.onNewClientClick();
    } else if (type === "warehouse_product_buy") {
      props.onWarehouseBuyClick();
    } else if (type === "warehouse_product_sell") {
      props.onWarehouseSellClick();
    }

    analyticsAction({
      category: "entry",
      action: `event_fab_click_${type}`,
    });
    toggle();
  };
  const subButtons = [
    props.onPersonalTaskClick && {
      icon: <NewPersonalTaskIcon width="18" />,
      type: "personal",
      name: props.t(ns + "task_private"),
    },
    props.onTodoClick && {
      icon: (
        <CheckmarkIcon
          style={{ position: "relative", top: "1px" }}
          width="12"
          color="currentColor"
        />
      ),
      type: "todo",
      name: props.t(ns + "todo"),
    },
    props.onNewClientClick && {
      icon: (
        <NewClientIcon
          style={{ position: "relative", left: "2px" }}
          width="18"
        />
      ),
      type: "client",
      name: props.t(ns + "client"),
    },
    props.onWarehouseSellClick && {
      icon: <NewWarehouseProductSellIcon width="18" />,
      type: "warehouse_product_sell",
      name: props.t(ns + "warehouse_product_sell"),
    },
    props.onWarehouseBuyClick && {
      icon: <NewWarehouseProductBuyIcon width="18" />,
      type: "warehouse_product_buy",
      name: props.t(ns + "warehouse_product_buy"),
    },
    props.onIncomeClick && {
      icon: (
        <span
          style={{
            fontWeight: 700,
            fontSize: 16,
            color: "currentColor",
            lineHeight: 0,
          }}
        >
          $
        </span>
      ),
      type: "income",
      name: props.t(ns + "income"),
    },
    props.onExpenseClick && {
      icon: (
        <span
          style={{
            fontWeight: 700,
            fontSize: 16,
            color: "currentColor",
            lineHeight: 0,
          }}
        >
          $
        </span>
      ),
      type: "expense",
      name: props.t(ns + "expense"),
    },
  ].filter(Boolean);
  return (
    <>
      <AnimatePresence initial={false}>
        {isOpen && (
          <S.Backdrop
            variants={backdropVariants}
            exit="exit"
            initial="initial"
            onClick={toggle}
            transition={{ duration: 0.2 }}
            animate="animate"
          />
        )}
      </AnimatePresence>
      <S.AddTaskButtonWrap className={props.className}>
        <S.MainButton onClick={isOpen ? () => onClick("business") : toggle}>
          <motion.span
            animate={{
              rotate: isOpen ? "90deg" : "0deg",
            }}
          >
            <PlusIcon width="14" color="white" />
          </motion.span>
          <AnimatePresence exitBeforeEnter initial={false}>
            {isOpen && (
              <S.EventName
                exit="exit"
                initial="initial"
                animate="animate"
                variants={eventNameVariants}
                key="task-title"
              >
                {props.t(ns + "task_business")}
              </S.EventName>
            )}
          </AnimatePresence>
        </S.MainButton>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div>
              {subButtons.map(({ icon, name, type }, idx) => (
                <>
                  <S.SubButton
                    variants={subButtonVariants}
                    custom={idx + 1}
                    onClick={() => onClick(type)}
                    exit="exit"
                    initial="initial"
                    animate="animate"
                    style={{
                      "--color": "var(--font-color-touchable)",
                      color: "var(--bg-touchable)",
                    }}
                  >
                    <S.EventName
                      exit="exit"
                      initial="initial"
                      animate="animate"
                      variants={eventNameVariants}
                      style={{
                        color: "currentColor",
                      }}
                    >
                      {name}
                    </S.EventName>
                    {icon}
                  </S.SubButton>
                </>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </S.AddTaskButtonWrap>
    </>
  );
};
NewEventFab.propTypes = {
  className: PT.string,

  onTodoClick: PT.func,
  onTaskClick: PT.func.isRequired,
  onExpenseClick: PT.func,
  onPersonalTaskClick: PT.func,
  t: PT.func.isRequired,
};

const NewClientIcon = ({ color = "currentColor", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    fill="none"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      fill={color}
      fill-rule="evenodd"
      d="M9.5 12.554c2.755 0 4.963-2.237 4.963-5.027S12.255 2.5 9.5 2.5 4.537 4.737 4.537 7.527s2.208 5.027 4.963 5.027Zm0 2.461c-4.046 0-7.5.647-7.5 3.231C2 20.83 5.433 21.5 9.5 21.5c4.045 0 7.5-.647 7.5-3.231 0-2.585-3.433-3.254-7.5-3.254Zm10.398-5.427H21.1c.495 0 .899.41.899.912a.907.907 0 0 1-.899.911h-1.203v1.177a.906.906 0 0 1-.899.912.907.907 0 0 1-.899-.912v-1.177h-1.201A.906.906 0 0 1 16 10.5c0-.502.403-.911.899-.911H18.1V8.412c0-.503.404-.912.899-.912.496 0 .899.41.899.912v1.176Z"
      clip-rule="evenodd"
    />
  </svg>
);
const NewPersonalTaskIcon = ({ color = "currentColor", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    fill="none"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      fill={color}
      fillRule="evenodd"
      d="m16.41 2.769.002.75c2.755.215 4.574 2.092 4.577 4.97L21 16.917c.004 3.138-1.968 5.069-5.128 5.074l-7.72.01c-3.14.004-5.137-1.973-5.141-5.12L3 8.553c-.004-2.898 1.752-4.77 4.506-5.023v-.75a.746.746 0 0 1 .758-.77.747.747 0 0 1 .762.769v.7l5.865-.009v-.7A.747.747 0 0 1 15.65 2a.753.753 0 0 1 .76.769ZM4.522 8.862l14.949-.02v-.35c-.043-2.15-1.121-3.277-3.056-3.445v.77c0 .43-.334.77-.758.77a.752.752 0 0 1-.762-.768v-.81l-5.865.008v.81c0 .43-.324.77-.759.77a.752.752 0 0 1-.76-.769l-.002-.77c-1.925.193-2.99 1.325-2.988 3.493l.001.31Zm10.719 4.542v.011c.01.46.385.809.84.799a.823.823 0 0 0 .789-.852.83.83 0 0 0-.82-.797.828.828 0 0 0-.81.84Zm.815 4.488a.848.848 0 0 1-.821-.848.833.833 0 0 1 .809-.852h.01c.463 0 .84.379.84.849a.844.844 0 0 1-.838.851Zm-4.883-4.472c.02.46.396.819.85.799a.823.823 0 0 0 .78-.86.816.816 0 0 0-.821-.8c-.454.02-.81.402-.809.861Zm.854 4.427a.823.823 0 0 1-.85-.799c0-.46.354-.84.809-.86a.815.815 0 0 1 .82.797c.02.461-.335.841-.779.862Zm-4.922-4.392c.02.46.396.82.85.799.445-.02.8-.4.778-.86a.815.815 0 0 0-.82-.8.852.852 0 0 0-.808.861Zm.854 4.397a.822.822 0 0 1-.85-.799.853.853 0 0 1 .809-.86.815.815 0 0 1 .82.798c.021.46-.333.841-.779.861Z"
      clipRule="evenodd"
    />
  </svg>
);

const NewWarehouseProductBuyIcon = ({ color = "currentColor", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    fill="none"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      fill={color}
      fillRule="evenodd"
      d="M11.886 2h5.04C19.444 2 21.5 4 21.5 6.44v11.12c0 2.45-2.055 4.44-4.595 4.44h-5.03c-2.519 0-4.584-1.99-4.584-4.43v-4.8h6.402l-1.652 1.6c-.31.3-.31.79 0 1.09a.81.81 0 0 0 .568.22c.196 0 .403-.07.558-.22l3.015-2.91a.736.736 0 0 0 .237-.55c0-.2-.082-.4-.237-.54l-3.015-2.91a.816.816 0 0 0-1.126 0c-.31.3-.31.79 0 1.09l1.652 1.59H7.291V6.45C7.29 4 9.356 2 11.886 2ZM2.5 12c0-.42.355-.77.781-.77h4.01v1.54H3.28A.776.776 0 0 1 2.5 12Z"
      clipRule="evenodd"
    />
  </svg>
);

const NewWarehouseProductSellIcon = ({ color = "currentColor", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    fill="none"
    viewBox="0 0 24 24"
    {...props}
  >
    <path
      fill={color}
      fillRule="evenodd"
      d="M9.895 11.23a.77.77 0 0 0-.783.77c0 .42.346.77.783.77H16v4.78C16 20 13.975 22 11.472 22H6.517C4.025 22 2 20.01 2 17.56V6.45C2 3.99 4.035 2 6.528 2h4.965C13.975 2 16 3.99 16 6.44v4.79H9.895Zm9.735-2.69 2.92 2.91a.764.764 0 0 1 0 1.09l-2.92 2.91c-.15.15-.35.23-.54.23a.773.773 0 0 1-.55-1.32l1.6-1.59H16v-1.54h4.14l-1.6-1.59c-.3-.3-.3-.79 0-1.09.3-.31.79-.31 1.09-.01Z"
      clipRule="evenodd"
    />
  </svg>
);

export default memo(NewEventFab);
